import { ReactNode, useId } from 'react';

import { Button, Modal } from '@braskai/ui-kit';

import { Check, Close } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { NativeVideoPlayer } from '@/shared/ui/NativeVideoPlayer';

export type FeaturePaywallProps = {
  title: string;
  badge: string;
  bullets: { id: string; content: ReactNode }[];
  choosePlanLabel: string;
  closeLabel: string;
  disabled?: boolean;
  experimentVariant?: 'A' | 'B';
  hideAction?: boolean;
  image: string;
  note?: ReactNode;
  secondaryAction?: ReactNode;
  subtitle: string;
  videoSrc?: string;
  onChoosePlan: () => void;
  onClose: () => void;
};

export const FeaturePaywall = ({
  title,
  badge,
  bullets,
  choosePlanLabel,
  closeLabel,
  disabled,
  experimentVariant,
  hideAction,
  image,
  note,
  secondaryAction,
  subtitle,
  videoSrc,
  onChoosePlan,
  onClose,
}: FeaturePaywallProps) => {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <Modal
      PaperProps={{
        sx: {
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: '24px',
          boxShadow: '0 20px 24px -4px rgba(16,24,40,0.08), 0 8px 8px -4px rgba(16,24,40,0.03)',
          margin: 16,
          maxWidth: 'calc(100vw - 32px) !important',
          padding: { mobile: '24px', tablet: '40px' },
          position: 'relative',
          width: '496px !important',
        },
      }}
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      data-paywall-experiment-variant={experimentVariant}
      scroll="body"
      open
      onClose={onClose}
    >
      <IconButton
        aria-label={closeLabel}
        data-testid="feature-paywall-close"
        sx={{ color: 'grey.400', height: 36, position: 'absolute', right: 24, top: 24, width: 36 }}
        onClick={onClose}
      >
        <Close sx={{ fontSize: 22 }} />
      </IconButton>
      <Box sx={{ paddingRight: 28 }}>
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'primary.100',
            borderRadius: '999px',
            color: 'primary.700',
            display: 'inline-flex',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.06em',
            lineHeight: '18px',
            minHeight: 30,
            padding: '6px 14px',
            textTransform: 'uppercase',
          }}
          component="span"
        >
          {badge}
        </Box>
      </Box>
      <Typography
        id={titleId}
        sx={{
          color: 'grey.900',
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          lineHeight: '38px',
          marginTop: 20,
          textWrap: 'pretty',
        }}
        component="h2"
      >
        {title}
      </Typography>
      <Typography
        id={descriptionId}
        sx={{ color: 'grey.600', fontSize: 16, lineHeight: '24px', marginTop: 10, textWrap: 'pretty' }}
      >
        {subtitle}
      </Typography>
      <Box
        sx={{
          backgroundColor: 'grey.100',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: '16px',
          height: videoSrc ? 'auto' : 192,
          marginTop: 24,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {videoSrc ? (
          <NativeVideoPlayer
            data-testid="feature-paywall-video"
            poster={image}
            src={videoSrc}
            sx={{ display: 'block', height: 'auto', width: '100%' }}
            autoPlay
            controls
            loop
            muted
          />
        ) : (
          <Box
            alt=""
            component="img"
            src={image}
            sx={{ display: 'block', height: '100%', objectFit: 'cover', width: '100%' }}
          />
        )}
      </Box>
      <Stack component="ul" gap={14} sx={{ listStyle: 'none', margin: '24px 0 0', padding: 0 }}>
        {bullets.map((bullet) => (
          <Stack key={bullet.id} alignItems="flex-start" component="li" direction="row" gap={12}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: 'primary.50',
                borderRadius: '50%',
                color: 'primary.600',
                display: 'flex',
                flexShrink: 0,
                height: 22,
                justifyContent: 'center',
                marginTop: 1,
                width: 22,
              }}
            >
              <Check sx={{ fontSize: 16 }} />
            </Box>
            <Typography component="span" sx={{ color: 'grey.700', fontSize: 15, lineHeight: '22px' }}>
              {bullet.content}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {note && (
        <Box
          sx={{
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: '12px',
            marginTop: 20,
            padding: '14px 16px',
          }}
        >
          <Typography sx={{ color: 'grey.600', fontSize: 14, fontWeight: 600, lineHeight: '20px' }}>{note}</Typography>
        </Box>
      )}
      {!hideAction && (
        <Stack gap={12} marginTop={28}>
          {secondaryAction}
          <Button
            data-testid="upgrade-plan-modal-upgrade-button"
            disabled={disabled}
            size="lg"
            sx={{ height: 56 }}
            variant="primary"
            fullWidth
            onClick={onChoosePlan}
          >
            {choosePlanLabel}
          </Button>
        </Stack>
      )}
    </Modal>
  );
};
